'use client'

import { AlertIcon } from '@repo/ui/AlertIcon'
import { Avatar } from '@repo/ui/Avatar'
import {
  BalanceDataGridFilter,
  ClientPortalDataGridFilter,
  DateDataGridFilter,
  FeeEarnerDataGridFilter,
  SingleSelectDataGridFilter,
  StatusDataGridFilter,
  TextFieldDataGridFilter,
  WorkTypeDataGridFilter
} from '@repo/clients-matters-tables/DataGridTableCustomFilters'
import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { CustomToolbarTableModule } from '@repo/clients-matters-tables/CustomToolbarTableModule'
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridRenderHeaderFilterProps,
  GridSortModel,
  GridColumnMenu,
  GridColumnMenuProps,
  gridColumnDefinitionsSelector,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import { formatCurrency } from '@repo/utils/numbers'
import { formatDate } from '@repo/utils/time'
import { LinkWithClientMatterNo } from '@repo/app-assets/LinkWithClientMatterNo'
import { LockIcon } from '@repo/ui/LockIcon'
import { LockUnlockedIcon } from '@repo/ui/LockUnlockedIcon'
import { MoreMenuCard } from '@repo/clients-matters-tables/MoreMenuCard'
import { Pill } from '@repo/ui/Pill'
import { TableClearFilters } from '@repo/ui/TableClearFilters'
import { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useMatterTableStore } from '@repo/store/useMatterTableStore'
import { useUserPreferencesStore } from '@repo/store/useUserPreferencesStore'
import { useWindowSize } from '@uidotdev/usehooks'
import { v4 as uuidv4 } from 'uuid'
import defaultMattersTableConfig from '@repo/store/defaultMattersTableConfig'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

export const MattersTableModule = () => {
  const { currentClientMatterNo } = useMattersStore()
  const { fetchMattersTableData, mattersTableData } = useMatterTableStore()
  const { Matters: mattersPreferences, updateUserPreference } = useUserPreferencesStore()
  const {
    setMattersTableFilters,
    setMattersTableFiltersFromPreferences,
    mattersTableFilters,
    resetMatterTableFilters,
    mattersTableFilterCount
  } = useDataGridFilterStore()
  const apiRef = useGridApiRef()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { height: windowHeight } = useWindowSize()
  const [tableHeight, setTableHeight] = useState(windowHeight ? windowHeight - 216 : 0)
  const [filtersReady, setFiltersReady] = useState(false)

  const tableConfig = mattersPreferences?.MATTERS_TABLE_CONFIG?.resolvedValue || defaultMattersTableConfig

  if (!tableConfig) console.error('No table config found')

  const OPERATOR_KEYS = new Set([
    'numberComparisonOperator',
    'dateComparisonOperator',
    'comparisonOperator',
    'operator'
  ])

  const isEmptyPrimitive = (x: unknown) => x === null || x === undefined || (typeof x === 'string' && x.trim() === '')

  const isFilterValueActive = (v: any): boolean => {
    if (isEmptyPrimitive(v)) return false
    if (Array.isArray(v)) return v.some((item) => !isEmptyPrimitive(item))
    switch (typeof v) {
      case 'boolean':
        return true
      case 'number':
        return true
      case 'string':
        return v.trim() !== ''
      case 'object': {
        if ('value' in v) return !isEmptyPrimitive((v as any).value)
        if ('date' in v) return !isEmptyPrimitive((v as any).date) // <-- critical for date filters
        return Object.entries(v as Record<string, any>).some(
          ([k, val]) => !OPERATOR_KEYS.has(k) && isFilterValueActive(val)
        )
      }
      default:
        return false
    }
  }

  const areFiltersEffectivelyEmpty = (f: Record<string, any> | null | undefined) => {
    if (!f) return true
    return !Object.values(f).some((v) => isFilterValueActive(v))
  }

  const emptyOutFilters = (f: Record<string, any> | null | undefined) => {
    if (!f) return {}
    const out: Record<string, any> = {}
    for (const [k, v] of Object.entries(f)) {
      if (v && typeof v === 'object') {
        if ('date' in v || 'dateComparisonOperator' in v) {
          out[k] = { date: null, dateComparisonOperator: 0 }
        } else if ('value' in v || 'numberComparisonOperator' in v) {
          out[k] = { value: null, numberComparisonOperator: 0 }
        } else {
          out[k] = null
        }
      } else {
        out[k] = null
      }
    }
    return out
  }

  const prefsHydratedRef = useRef(false)
  const firstSyncDoneRef = useRef(false)
  const userClearedRef = useRef(false)

  useEffect(() => {
    if (!tableConfig) return

    const storeEmpty = areFiltersEffectivelyEmpty(mattersTableFilters?.filters)
    const prefsEmpty = areFiltersEffectivelyEmpty(tableConfig?.filters)

    if (prefsHydratedRef.current) {
      setFiltersReady(true)
      return
    }

    if (!storeEmpty) {
      prefsHydratedRef.current = true
      setFiltersReady(true)
      return
    }

    if (!prefsEmpty) {
      setMattersTableFiltersFromPreferences({ filters: tableConfig.filters ?? null })
      prefsHydratedRef.current = true
      setFiltersReady(true)
      return
    }

    setFiltersReady(true)
  }, [tableConfig, tableConfig?.filters, mattersTableFilters?.filters, setMattersTableFiltersFromPreferences])

  const [loading, setLoading] = useState(false)
  const [columnOrder, setColumnOrder] = useState(tableConfig?.columnOrder || [])
  const [columnWidths, setColumnWidths] = useState(tableConfig?.columnWidths || {})
  const [sortModel, setSortModel] = useState<GridSortModel>(tableConfig?.columnSort || [])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    tableConfig?.columnVisibilityModel || {}
  )
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>({
    left: tableConfig?.columnsPinnedLeft || [],
    right: tableConfig?.columnsPinnedRight || []
  })

  const updateTableConfigPreference = (fieldsToChange: any) => {
    const tableConfigPreferenceId = mattersPreferences?.MATTERS_TABLE_CONFIG?.userConfigurationTypeId || null
    if (!tableConfigPreferenceId) return

    updateUserPreference({
      userConfigurationTypeId: tableConfigPreferenceId,
      value: {
        ...tableConfig,
        columnOrder: columnOrder,
        columnVisibilityModel: columnVisibilityModel,
        columnWidths: columnWidths,
        columnsPinnedLeft: pinnedColumns.left,
        columnsPinnedRight: pinnedColumns.right,
        columnSort: sortModel,
        ...fieldsToChange
      },
      convertJsonToString: true,
      categoryName: 'Matters'
    })
  }

  const onPinnedColumnsChange = useCallback((updatedPinnedColumns: GridPinnedColumnFields) => {
    setPinnedColumns(updatedPinnedColumns)
    updateTableConfigPreference({
      columnsPinnedLeft: updatedPinnedColumns.left,
      columnsPinnedRight: updatedPinnedColumns.right
    })
  }, [])

  const onSortModelChange = (model: any) => {
    setSortModel(model)
    updateTableConfigPreference({ columnSort: model })
  }

  const onColumnVisibilityModelChange = (model: any) => {
    setColumnVisibilityModel(model)
    updateTableConfigPreference({ columnVisibilityModel: model })
  }

  const onColumnOrderChange = () => {
    const columnDefinitions = gridColumnDefinitionsSelector(apiRef)
    const columnOrder = columnDefinitions.map((column: any) => column.field)
    setColumnOrder(columnOrder)
    updateTableConfigPreference({ columnOrder })
  }

  const onColumnWidthChange = () => {
    const columnDefinitions = gridColumnDefinitionsSelector(apiRef)
    const columnWidths: any = {}
    columnDefinitions.forEach((column: any) => {
      columnWidths[column.field] = column.width
    })
    setColumnWidths(columnWidths)
    updateTableConfigPreference({ columnWidths })
  }

  const useDeepCompareMemoize = (value: any) => {
    const ref = useRef(value)

    // Stringify the value (for small objects this is OK)
    const signal = JSON.stringify(value)
    const prevSignal = JSON.stringify(ref.current)

    if (signal !== prevSignal) {
      ref.current = value
    }

    return ref.current
  }

  const deepFilters = useDeepCompareMemoize(mattersTableFilters)

  useEffect(() => {
    if (!filtersReady) return

    const getTableData = async () => {
      setLoading(true)

      // Compute the new orderByColumns array from the sort model.
      const computedOrderBy = sortModel
        .map((sortItem) => {
          // Skip "moreMenu" if it's not meant to be sorted.
          if (sortItem.field === 'moreMenu') return null
          // Map clientMatterNo to clientNo.
          if (sortItem.field === 'clientMatterNo') {
            return { columnName: 'clientNo', direction: sortItem.sort === 'asc' ? 0 : 1 }
          }
          return { columnName: sortItem.field, direction: sortItem.sort === 'asc' ? 0 : 1 }
        })
        .filter(Boolean)

      // If any orderBy item has a null columnName, then set orderByColumns to null.
      const orderByColumns = computedOrderBy.some((item: any) => item.columnName === null) ? null : computedOrderBy

      setMattersTableFilters({
        clientMatterNo: 'clientMatterNo',
        value: currentClientMatterNo
      })

      setMattersTableFilters({
        orderByColumns: 'orderByColumns',
        value: orderByColumns
      })

      setMattersTableFilters({
        startAtIndex: 'startAtIndex',
        value: deepFilters.startAtIndex ?? 0
      })

      setMattersTableFilters({
        pageSize: 'pageSize',
        value: deepFilters.pageSize ?? 25
      })

      const deepFiltersFiltersString = JSON.stringify(deepFilters.filters)
      const currentTableConfigFiltersString = JSON.stringify(tableConfig.filters)
      const filtersHaveChanged = deepFiltersFiltersString !== currentTableConfigFiltersString

      const deepEmpty = areFiltersEffectivelyEmpty(deepFilters.filters)
      const prefsEmpty = areFiltersEffectivelyEmpty(tableConfig.filters)
      const safeToSync = firstSyncDoneRef.current ? true : false // skip first time

      if (safeToSync && filtersHaveChanged) {
        // Also optional: if you're still nervous, gate with !(deepEmpty && !prefsEmpty)
        !(deepEmpty && !prefsEmpty) && updateTableConfigPreference({ filters: deepFilters.filters })
      }

      await fetchMattersTableData()

      setLoading(false)

      if (!firstSyncDoneRef.current) firstSyncDoneRef.current = true
    }

    getTableData()
  }, [filtersReady, deepFilters.pageSize, deepFilters.filters, deepFilters.startAtIndex, sortModel])

  useEffect(() => {
    setMattersTableFilters({ totalRowCount: 'totalRowCount', value: mattersTableData.totalRowCount || 0 })
  }, [mattersTableData.totalRowCount, setMattersTableFilters])

  useEffect(() => {
    const offset = pageTitleBlockIsVisible ? 216 : 146
    if (windowHeight) setTableHeight(windowHeight - offset)
  }, [windowHeight, pageTitleBlockIsVisible])

  const handleClearAll = useCallback(() => {
    userClearedRef.current = true
    // clear the store
    resetMatterTableFilters()
    // clear saved prefs (preserve shapes for date/number objects)
    const emptied = emptyOutFilters(tableConfig?.filters ?? mattersTableFilters?.filters ?? {})
    updateTableConfigPreference({ filters: emptied })
  }, [resetMatterTableFilters, tableConfig?.filters, mattersTableFilters?.filters])

  const columns: any = [
    {
      field: 'isUserRestricted',
      // @ts-ignore
      id: 'isUserRestricted',
      headerName: 'User Restricted',
      headerClassName: 'user-restricted-header-name',
      type: 'boolean',
      width: columnWidths?.['isUserRestricted'] || 30,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      align: 'right',
      renderCell: (params: any) => {
        return params.value === true ? (
          <LockIcon sx={{ width: '14px', height: '14px' }} />
        ) : (
          <LockUnlockedIcon sx={{ width: '14px', height: '14px' }} />
        )
      }
    },
    {
      field: 'clientMatterNo',
      // @ts-ignore
      id: 'clientMatterNo',
      headerName: 'Matter no',
      width: columnWidths?.['clientMatterNo'] || 150,
      type: 'string',
      hideable: false,
      renderCell: (params: any) => (
        <ClientMatterDetailsPill
          matterNo={params.row.matterNo}
          clientNo={params.row.clientNo}
          sx={{ top: '3px' }}
          isUserRestricted={params.row.isUserRestricted}
        />
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter filterType="clientNo" {...params} />
      )
    },
    {
      field: 'matterDescription',
      headerName: 'Matter description',
      width: columnWidths?.['matterDescription'] || 280,
      type: 'string',
      renderCell: (params: any) => {
        return (
          <LinkWithClientMatterNo
            clientMatterNo={`${params.row.clientNo}-${params.row.matterNo}`}
            // no 'to' prop, so it will go to the default matter page
          >
            <MUITypography
              variant="body2"
              sx={{
                mb: 0,
                cursor: params.row.isUserRestricted ? 'not-allowed' : 'pointer',
                textDecoration: params.row.isUserRestricted ? 'none' : 'underline',
                textTransform: 'capitalize',
                fontSize: '0.875rem',
                whiteSpace: 'wrap',
                lineHeight: '1.2rem'
              }}
            >
              {params.value}
            </MUITypography>
          </LinkWithClientMatterNo>
        )
      },
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter filterType="matterDescription" {...params} />
      )
    },
    {
      field: 'clientNames',
      headerName: 'Client names',
      width: columnWidths?.['clientNames'] || 280,
      type: 'string',
      renderCell: (params: any) => {
        const clientNames = params.row.clientNames

        if (clientNames.length > 1) {
          return (
            <MUITooltip
              placement="bottom"
              arrow
              sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
              title={clientNames.map((client: any) => (
                <MUITypography
                  key={client.name}
                  variant="body2"
                  color="white"
                  sx={{ mb: 1, p: 1, fontSize: '0.75rem' }}
                >
                  {client.name}
                </MUITypography>
              ))}
            >
              <MUIStack display="flex" direction="row" gap={1.5} sx={{ cursor: 'pointer' }}>
                <MUITypography variant="body2" sx={{ cursor: 'pointer', mb: 0, fontSize: '0.875rem' }}>
                  {clientNames[0].name}
                </MUITypography>
                <MUITypography variant="body2" sx={{ cursor: 'pointer', mb: 0, fontSize: '0.875rem' }}>
                  +{clientNames.length - 1}
                </MUITypography>
              </MUIStack>
            </MUITooltip>
          )
        } else {
          return (
            <MUIStack display="flex" direction="row" gap={1.5}>
              {clientNames.map((client: any) => (
                <MUITypography key={client.name} variant="body2" sx={{ fontSize: '0.875rem', mb: 0 }}>
                  {client.name !== '' ? client.name : '-'}
                </MUITypography>
              ))}
            </MUIStack>
          )
        }
      },
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter filterType="clientName" {...params} />
      )
    },
    {
      field: 'workTypeDescription',
      headerName: 'Work type',
      width: columnWidths?.['workTypeDescription'] || 280,
      type: 'custom',
      renderCell: (params: any) => (
        <MUITooltip
          placement="bottom"
          arrow
          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
          title={params.value}
        >
          <MUITypography
            variant="body2"
            sx={{
              cursor: 'pointer',
              backgroundColor: '#ECEEF2',
              borderRadius: '1rem',
              fontSize: '0.75rem',
              mb: 0,
              padding: '0.25rem 0.5rem',
              width: 'max-content',
              position: 'relative',
              top: '0.85rem'
            }}
          >
            {params.value}
          </MUITypography>
        </MUITooltip>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <WorkTypeDataGridFilter idName="workflowTypeId" filterType="workTypeIds" {...params} />
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: columnWidths?.['status'] || 280,
      type: 'singleSelect',
      renderCell: (params: any) => (
        <MUIStack display="flex" direction="row" gap={1.5} sx={{ position: 'relative' }}>
          {params.row.isCompleted === true ? (
            <Pill
              label="Completed"
              color={'white'}
              backgroundColor={'success.main'}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          ) : null}
          {params.row.isCompleted !== true && params.row.isArchived !== true ? (
            <Pill
              label="Open"
              color={'white'}
              backgroundColor={'secondary.main'}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          ) : null}
          {params.row.isArchived === true && (
            <Pill
              label="Archived"
              color={'black'}
              backgroundColor={'background.mid'}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          )}
          {params.row.isProspect === true && (
            <Pill
              label="Prospect"
              backgroundColor="support.sand"
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          )}
          {params.row.highRisk === true ? (
            <MUITypography
              sx={{
                alignItems: 'center',
                backgroundColor: '#C33241',
                borderRadius: '1rem',
                color: '#fff',
                display: 'flex',
                fontSize: '0.75rem',
                fontWeight: '600',
                height: '25px',
                justifyContent: 'center',
                mb: 0,
                textAlign: 'center',
                width: '94px',
                minWidth: '94px'
              }}
              variant="body2"
            >
              <AlertIcon
                sx={{
                  height: '1rem',
                  mr: '0.25rem',
                  width: '1rem',
                  [`& > path`]: { fill: '#fff', strokeWidth: '2px' }
                }}
              />{' '}
              High Risk
            </MUITypography>
          ) : null}
        </MUIStack>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => <StatusDataGridFilter {...params} />
    },

    {
      field: 'workflowStatus',
      headerName: 'Workflow status',
      width: columnWidths?.['workflowStatus'] || 220,
      type: 'string',
      filterable: false,
      sortable: false,
      renderCell: (params: any) =>
        params.value ? (
          <MUITooltip
            placement="bottom"
            arrow
            sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
            title={params.value}
          >
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }} noWrap>
              {params.value}
            </MUITypography>
          </MUITooltip>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'feeEarnerDescription',
      headerName: 'Fee earner',
      width: columnWidths?.['feeEarnerDescription'] || 220,
      type: 'custom',
      renderCell: (params: any) => {
        return (
          <MUIStack display="flex" direction="row" sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
            <Avatar
              feeEarnerId={params.row.feeEarnerId}
              name={params.value}
              sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }}
            />
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
              {params.value}
            </MUITypography>
          </MUIStack>
        )
      },
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <FeeEarnerDataGridFilter filterType="feeEarnerIds" {...params} />
      )
    },
    {
      field: 'supervisingFeeEarnerDescription',
      headerName: 'Supervising fee earner',
      width: columnWidths?.['supervisingFeeEarnerDescription'] || 220,
      type: 'singleSelect',
      renderCell: (params: any) => (
        <MUIStack display="flex" direction="row" sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
          {params.value === null ? (
            <MUIStack display="flex" direction="row" sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
              <Avatar name="" sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }} />
              <MUITypography variant="body2" sx={{ mt: '8px', fontSize: '0.875rem' }}>
                -
              </MUITypography>
            </MUIStack>
          ) : (
            <MUIStack display="flex" direction="row" sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
              <Avatar
                feeEarnerId={params.row.supervisingFeeEarnerId}
                name={params.value}
                sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }}
              />
              <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
                {params.value}
              </MUITypography>
            </MUIStack>
          )}
        </MUIStack>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <FeeEarnerDataGridFilter filterType="supervisingFeeEarnerIds" {...params} />
      )
    },
    {
      field: 'privateOrLegalAid',
      headerName: 'Private or legal aid',
      width: columnWidths?.['privateOrLegalAid'] || 200,
      type: 'custom',
      renderCell: (params: any) =>
        params.value === 'P' ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            Private
          </MUITypography>
        ) : params.value === 'L' ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            Legal Aid
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            Not set
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <SingleSelectDataGridFilter
          filterType="privateOrLegalAid"
          label="Select"
          valueMapping={{ P: 'Private', L: 'Legal aid' }}
          options={['P', 'L']}
          {...params}
        />
      )
    },
    {
      field: 'clientType',
      headerName: 'Client type',
      width: columnWidths?.['clientType'] || 200,
      type: 'singleSelect',
      renderCell: (params: any) =>
        params.value === 'C' ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            Company
          </MUITypography>
        ) : params.value === 'I' ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            Individual
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            Not set
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <SingleSelectDataGridFilter
          filterType="clientType"
          label="Select"
          options={['C', 'I']}
          valueMapping={{ C: 'Company', I: 'Individual' }}
          {...params}
        />
      )
    },
    {
      field: 'branchDescription',
      headerName: 'Branch',
      width: columnWidths?.['branchDescription'] || 220,
      type: 'string',
      filterable: false,
      sortable: false,
      renderCell: (params: any) =>
        params.value ? (
          <MUITooltip
            placement="bottom"
            arrow
            sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
            title={params.value}
          >
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }} noWrap>
              {params.value}
            </MUITypography>
          </MUITooltip>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'departmentDescription',
      headerName: 'Department',
      width: columnWidths?.['departmentDescription'] || 220,
      type: 'custom',
      filterable: false,
      sortable: false,
      renderCell: (params: any) =>
        params.value ? (
          <MUITooltip
            placement="bottom"
            arrow
            sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
            title={params.value}
          >
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }} noWrap>
              {params.value}
            </MUITypography>
          </MUITooltip>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'isClientPortalEnabled',
      headerName: 'Client portal access',
      width: columnWidths?.['isClientPortalEnabled'] || 170,
      type: 'singleSelect',
      renderCell: (params: any) => (
        <MUITypography
          sx={{
            backgroundColor: params.value === true ? '#5073c3' : 'rgba(195, 50, 65, 0.08)',
            borderRadius: '1rem',
            color: params.value === true ? '#fff' : '#c33241',
            fontSize: '0.75rem',
            fontWeight: '700',
            height: '22px',
            marginRight: '1rem',
            paddingTop: '1px',
            textAlign: 'center',
            transition: 'background-color 0.3s ease, color 0.3s ease, width 0.3s ease',
            width: params.value === true ? '67px' : '91px'
          }}
        >
          {params.value === true ? 'Enabled' : 'Not enabled'}
        </MUITypography>
      ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => <ClientPortalDataGridFilter {...params} />
    },
    {
      field: 'dateOpened',
      headerName: 'Date opened',
      width: columnWidths?.['dateOpened'] || 190,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatDate(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <DateDataGridFilter filterType="dateOpened" {...params} />
      )
    },
    {
      field: 'prospectLiveDate',
      headerName: 'Date prospect went live',
      width: columnWidths?.['prospectLiveDate'] || 190,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatDate(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <DateDataGridFilter filterType="prospectLiveDate" {...params} />
      )
    },
    {
      field: 'dateCompleted',
      headerName: 'Date completed',
      width: columnWidths?.['dateCompleted'] || 190,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatDate(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <DateDataGridFilter filterType="dateCompleted" {...params} />
      )
    },
    {
      field: 'dateArchived',
      headerName: 'Date archived',
      width: columnWidths?.['dateArchived'] || 190,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatDate(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <DateDataGridFilter filterType="dateArchived" {...params} />
      )
    },
    {
      field: 'archiveLocation',
      headerName: 'Archive location',
      width: columnWidths?.['archiveLocation'] || 170,
      type: 'string',
      renderCell: (params: any) =>
        params.value ? (
          <MUITooltip
            placement="bottom"
            arrow
            sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
            title={params.value}
          >
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
              {params.value}
            </MUITypography>
          </MUITooltip>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter filterType="archiveLocation" {...params} />
      )
    },
    {
      field: 'archiveReference',
      headerName: 'Archive reference',
      width: columnWidths?.['archiveReference'] || 170,
      type: 'string',
      renderCell: (params: any) =>
        params.value ? (
          <MUITooltip
            placement="bottom"
            arrow
            sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
            title={params.value}
          >
            <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
              {params.value}
            </MUITypography>
          </MUITooltip>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <TextFieldDataGridFilter filterType="archiveLocation" {...params} />
      )
    },
    {
      field: 'reviewDate',
      headerName: 'Review date',
      width: columnWidths?.['reviewDate'] || 170,
      type: 'custom',
      filterable: false,
      sortable: false,
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatDate(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'reviewPeriod',
      headerName: 'Review period',
      width: columnWidths?.['reviewPeriod'] || 170,
      filterable: false,
      sortable: false,
      type: 'custom',
      renderCell: (params: any) => (
        <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
          {params.value} days
        </MUITypography>
      )
    },
    {
      field: 'lastMatterDate',
      headerName: 'LMD',
      width: columnWidths?.['lastMatterDate'] || 190,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatDate(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <DateDataGridFilter filterType="lastMatterDate" {...params} />
      )
    },
    {
      field: 'nextKeyDate',
      headerName: 'Next key date',
      width: columnWidths?.['nextKeyDate'] || 190,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatDate(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <DateDataGridFilter filterType="nextKeyDate" {...params} />
      )
    },

    {
      field: 'clientDebtorLimit',
      headerName: 'Client debtor limit',
      width: columnWidths?.['clientDebtorLimit'] || 170,
      filterable: false,
      sortable: false,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatCurrency(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'clientDisbursementsLimit',
      headerName: 'Client disbs limit',
      width: columnWidths?.['clientDisbursementsLimit'] || 170,
      filterable: false,
      sortable: false,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatCurrency(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'clientWipLimit',
      headerName: 'Client WIP limit',
      width: columnWidths?.['clientWipLimit'] || 170,
      filterable: false,
      sortable: false,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatCurrency(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'matterDebtorLimit',
      headerName: 'Matter debtor limit',
      width: columnWidths?.['matterDebtorLimit'] || 170,
      filterable: false,
      sortable: false,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatCurrency(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'matterDisbursementsLimit',
      headerName: 'Matter disbs limit',
      width: columnWidths?.['matterDisbursementsLimit'] || 170,
      filterable: false,
      sortable: false,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatCurrency(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'matterWipLimit',
      headerName: 'Matter WIP limit',
      width: columnWidths?.['matterWipLimit'] || 170,
      filterable: false,
      sortable: false,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatCurrency(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )
    },
    {
      field: 'matterWipBalance',
      headerName: 'WIP balance',
      width: columnWidths?.['matterWipBalance'] || 220,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatCurrency(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <BalanceDataGridFilter filterType="matterWipBalance" {...params} />
      )
    },
    {
      field: 'officeBalance',
      headerName: 'Office balance',
      width: columnWidths?.['officeBalance'] || 220,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatCurrency(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <BalanceDataGridFilter filterType="officeBalance" {...params} />
      )
    },
    {
      field: 'clientBalance',
      headerName: 'Client balance',
      width: columnWidths?.['clientBalance'] || 220,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatCurrency(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <BalanceDataGridFilter filterType="clientBalance" {...params} />
      )
    },
    {
      field: 'disbursementsBalance',
      headerName: 'Disbs balance',
      width: columnWidths?.['disbursementsBalance'] || 220,
      type: 'custom',
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatCurrency(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: (params: GridRenderHeaderFilterProps) => (
        <BalanceDataGridFilter filterType="disbursementsBalance" {...params} />
      )
    },
    {
      field: 'moreMenu',
      headerName: '',
      width: columnWidths?.['moreMenu'] || 49,
      hideable: false,
      filterable: false,
      sortable: false,
      type: 'actions',
      renderCell: (params: any) => (
        <MoreMenuCard
          isClientTable={false}
          clientNo={params.row.clientNo}
          matterNo={params.row.matterNo}
          rowData={params.row}
          disabled={params.row.isUserRestricted}
        />
      )
    }
  ].sort((a, b) => {
    // sort the columns as per the order in the tableConfig
    const targetIndexA = columnOrder?.indexOf(a.field) || 0
    const targetIndexB = columnOrder?.indexOf(b.field) || 1

    if (targetIndexA > targetIndexB) return 1
    return -1
  })

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => column.hideable !== false && column.field !== '__check__')
      .map((column) => column.field)
  }

  const paginationModel: GridPaginationModel = {
    page: Math.floor(
      (mattersTableFilters.startAtIndex === undefined || mattersTableFilters.startAtIndex === null
        ? 0
        : mattersTableFilters.startAtIndex) /
        (mattersTableFilters.pageSize === undefined || mattersTableFilters.pageSize === null
          ? 25
          : mattersTableFilters.pageSize)
    ),
    pageSize:
      mattersTableFilters.pageSize === undefined || mattersTableFilters.pageSize === null
        ? 25
        : mattersTableFilters.pageSize
  }

  return (
    <MUIPaper
      sx={{
        position: 'relative',
        fontSize: '0.875rem',
        height: `calc(${tableHeight}px - 20px)` || 'auto',
        width: '100%',
        mt: '9rem',
        boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0)',
        [`@media (min-height: 991px)`]: { mt: '-3.5rem' },
        [`& .MuiDataGrid-root`]: {
          border: 'none',
          [`& div[field="clientNames"]`]: { cursor: 'pointer', [`& .MuiStack-root`]: { cursor: 'pointer' } }
        },
        '& .MuiDataGrid-columnsPanel .MuiDataGrid-panelHeader': {
          display: 'none'
        }
      }}
    >
      {filtersReady && (
        <>
          <TableClearFilters
            filterCount={mattersTableFilterCount}
            onClear={handleClearAll}
            sx={{ position: 'absolute', top: '3px', right: '130px', zIndex: 2 }}
          />
          <DataGridPro
            apiRef={apiRef}
            columns={columns}
            onColumnOrderChange={onColumnOrderChange}
            onColumnWidthChange={onColumnWidthChange}
            columnVisibilityModel={columnVisibilityModel}
            disableVirtualization
            getRowId={(row) => `${row.clientNo}-${row.matterNo}-${uuidv4()}`}
            headerFilters
            loading={loading}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            onPaginationModelChange={(newModel) => {
              setMattersTableFilters({
                pageSize: 'pageSize',
                value: newModel.pageSize
              })
              setMattersTableFilters({
                startAtIndex: 'startAtIndex',
                value: newModel.page * newModel.pageSize
              })
              setMattersTableFilters({
                totalRowCount: 'totalRowCount',
                value: mattersTableData.totalRowCount
              })
            }}
            onPinnedColumnsChange={onPinnedColumnsChange}
            onSortModelChange={onSortModelChange}
            pageSizeOptions={[5, 10, 25, 50]}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            pinnedColumns={pinnedColumns}
            rowCount={mattersTableData?.totalRowCount || 0}
            rows={mattersTableData.rows ?? []}
            slotProps={{
              columnsManagement: { getTogglableColumns },
              loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' }
            }}
            sortModel={sortModel}
            sx={{
              [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
              [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                outline: 'none'
              },
              [`* .${gridClasses.columnHeader}`]: { fontSize: '0.875rem' },
              [`& .MuiDataGrid-cell`]: { alignContent: 'center', [`& .MuiTypography-root`]: { top: 'unset' } },
              [`& .MuiTablePagination-selectLabel`]: {
                display: 'block !important'
              },

              [`& .MuiTablePagination-input`]: {
                display: 'inline-flex !important',
                mb: 0,
                ml: 0,
                mt: '8px',
                fontSize: '0.875rem',
                [`& .MuiTablePagination-select`]: {
                  padding: '0 !important',
                  ml: '4px',
                  mr: '20px',
                  minWidth: 'none',
                  fontSize: '12px',
                  [`&:focus`]: { backgroundColor: 'transparent' }
                },
                [`& .MuiSvgIcon-root`]: { width: '1.5rem', height: '1.5rem', top: 'calc(50% - 12px)' }
              },
              [`& .MuiTablePagination-actions`]: { [`& .MuiButtonBase-root`]: { padding: 0, top: '4px' } },
              [`& .user-restricted-header-name`]: { [`& .MuiDataGrid-columnHeaderTitle`]: { display: 'none' } },
              [`& .MuiDataGrid-filler`]: {
                display: 'none'
              }
            }}
            slots={{
              columnMenu: (props: GridColumnMenuProps) => (
                <GridColumnMenu
                  {...props}
                  // @ts-ignore
                  sx={{ '.MuiTypography-body1': { mb: 0 } }}
                  slots={{ columnMenuFilterItem: null }} // Hides the "Filters" item
                />
              ),
              headerFilterMenu: null,
              toolbar: () => <CustomToolbarTableModule isMatterTable />
            }}
          />
        </>
      )}
    </MUIPaper>
  )
}
